import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaUserPlus,
  FaEnvelope,
  FaCalendarAlt,
  FaUsers,
  FaPlus,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import {
  setSegments,
  setContacts,
  setAllData,
  setLoading,
  setError,
} from "../../../state/reducers/communicationSlice";
import {
  getCampaigns,
  getSegments,
} from "../../../services/communication.services";

function Home() {
  const dispatch = useDispatch();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const { segments, contacts, allData } = useSelector(
    (state) => state.communication
  );
  const navigate = useNavigate();
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaigns();
      setCampaigns(response);
    } catch (err) {
      setError(err.message || "Failed to fetch campaigns.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchSegments = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getSegments();
      if (response && response.segment_counts) {
        dispatch(setAllData(response));
      } else {
        dispatch(setError("Unexpected response structure from the server."));
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to fetch segments."));
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    if (!segments || segments.length === 0) {
      fetchSegments();
    }
  }, [dispatch, segments]);

  const sortedCampaigns = campaigns.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  // Campaign Card Component for better mobile layout
  const CampaignCard = ({ campaign }) => (
    <div className="border border-gray-200 rounded-lg p-4 mb-4 hover:shadow-md transition-shadow bg-white">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3">
        <div className="mb-2 sm:mb-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
            {campaign.name}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500">
            #{campaign.id} · Sent on{" "}
            {new Date(campaign.created_at).toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-600">Total Contacts</div>
          <div className="text-lg sm:text-xl font-bold text-gray-900">
            {campaign.emails_sent}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✓ Sent
        </span>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <FaEnvelope className="mr-1" />
          {campaign.channel}
        </span>
      </div>
    </div>
  );

  // Contact Stats Card Component
  const ContactStatsCard = ({
    title,
    value,
    subtitle,
    icon,
    bgColor = "bg-gray-100",
    iconColor = "text-gray-600",
  }) => (
    <div className="flex justify-between items-center p-4 sm:p-6 border-2 rounded-lg border-gray-200 bg-white hover:shadow-sm transition-shadow">
      <div>
        <h3 className="text-lg sm:text-xl font-bold text-gray-900">
          {value || "0"}
        </h3>
        <p className="text-sm sm:text-base font-medium text-gray-600 mt-1">
          {title}
        </p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`${bgColor} ${iconColor} p-2 sm:p-3 rounded-lg`}>
        {icon}
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 transition-all duration-300 ${
        isSideBarOpen ? "ml-0 lg:ml-80" : "ml-0 md:ml-20"
      }`}
    >
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Dashboard
          </h1>
          <p className="text-gray-600">
            Overview of your campaigns and contacts
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Section: Campaigns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                    Recent Campaigns
                  </h2>
                  <p className="text-sm text-gray-600">
                    Your latest email campaigns
                  </p>
                </div>
                <button
                  onClick={() => navigate("/admin/communications/campaigns")}
                  className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FaPlus className="mr-2" />
                  Create Campaign
                </button>
              </div>

              {/* Campaign List */}
              <div className="p-4 sm:p-6">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <span className="ml-3 text-gray-600">
                      Loading campaigns...
                    </span>
                  </div>
                ) : sortedCampaigns.length > 0 ? (
                  <div className="space-y-4">
                    {sortedCampaigns.slice(0, 3).map((campaign) => (
                      <CampaignCard key={campaign.id} campaign={campaign} />
                    ))}
                    {sortedCampaigns.length > 3 && (
                      <div className="text-center pt-4">
                        <button
                          onClick={() =>
                            navigate("/admin/communications/campaigns")
                          }
                          className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                        >
                          View all campaigns ({sortedCampaigns.length})
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaEnvelope className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No campaigns yet
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Create your first email campaign to get started
                    </p>
                    <button
                      onClick={() =>
                        navigate("/admin/communications/campaigns")
                      }
                      className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <FaPlus className="mr-2" />
                      Create First Campaign
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Section: Contacts */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Header */}
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row justify-between items-start sm:items-center lg:items-start xl:items-center p-4 sm:p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                    Your Contacts
                  </h2>
                  <p className="text-sm text-gray-600">Contact statistics</p>
                </div>
                <button
                  onClick={() => navigate("/admin/communications/contacts")}
                  className="mt-3 sm:mt-0 lg:mt-3 xl:mt-0 text-purple-600 hover:text-purple-700 font-medium text-sm"
                >
                  Add Contact
                </button>
              </div>

              {/* Contact Stats */}
              <div className="p-4 sm:p-6 space-y-4">
                <ContactStatsCard
                  title="Total Contacts"
                  value={allData?.total_contacts}
                  icon={<FaUsers className="text-lg sm:text-xl" />}
                  bgColor="bg-blue-50"
                  iconColor="text-blue-600"
                />

                <ContactStatsCard
                  title="New Contacts"
                  value={allData?.contact_last_30_days}
                  subtitle="Last 30 days"
                  icon={<FaUserPlus className="text-lg sm:text-xl" />}
                  bgColor="bg-green-50"
                  iconColor="text-green-600"
                />

                {/* Action Buttons */}
                <div className="pt-4 space-y-2">
                  <button
                    onClick={() => navigate("/admin/communications/contacts")}
                    className="w-full inline-flex items-center justify-center px-4 py-3 bg-gray-50 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FaUsers className="mr-2" />
                    Manage Contacts
                  </button>
                  {/* <button
                    onClick={() => navigate("/admin/communications/segments")}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    View Segments
                    <FaArrowRight className="ml-2" />
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Mobile Bottom Navigation Style */}
        <div className="mt-6 lg:hidden">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate("/admin/communications/campaigns")}
                className="flex flex-col items-center p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <FaEnvelope className="text-xl mb-2" />
                <span className="text-sm font-medium">Campaigns</span>
              </button>
              <button
                onClick={() => navigate("/admin/communications/contacts")}
                className="flex flex-col items-center p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <FaUsers className="text-xl mb-2" />
                <span className="text-sm font-medium">Contacts</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
